/**
 * O tour: o manual apontando para a tela viva.
 *
 * **Por que não é print de tela.** Print envelhece calado — no dia em que um
 * campo muda de lugar, a imagem continua ali parecendo autoridade, e ninguém
 * descobre porque nada quebra. Aqui não existe cópia: o tour procura o
 * elemento de verdade, na tela de verdade, com os dados da própria escola. E
 * quando o elemento não existe mais, isso aparece — em `test/ajuda.test.ts`
 * para os campos, e nesta tela para as marcas, que dizem em voz alta que a
 * tela mudou em vez de fingir que o passo correu bem.
 *
 * **Como o alvo é dito.** O catálogo nunca guarda seletor de CSS: ele diz
 * `campo` (o nome da coluna) ou `marca` (um rótulo que a tela declara em
 * `data-tour`). A tradução para seletor acontece aqui, e em nenhum outro
 * lugar.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import type { PassoDoTour } from '../api'

/** Quanto de respiro entre o contorno e o elemento. */
const FOLGA = 6

/** O passo diz o alvo em termos de domínio; aqui ele vira seletor. */
function seletorDe(passo: PassoDoTour): string | null {
  if (passo.campo) return `[data-campo="${CSS.escape(passo.campo)}"]`
  if (passo.marca) return `[data-tour="${CSS.escape(passo.marca)}"]`
  return null
}

interface Caixa {
  topo: number
  esquerda: number
  largura: number
  altura: number
}

export function Tour({ passos, aoFechar }: { passos: PassoDoTour[]; aoFechar: () => void }) {
  const [i, setI] = useState(0)
  const [caixa, setCaixa] = useState<Caixa | null>(null)
  const [sumiu, setSumiu] = useState(false)
  const balaoRef = useRef<HTMLDivElement>(null)

  const passo = passos[i]
  const ultimo = i === passos.length - 1

  /** Acha o alvo, rola até ele e mede onde ele está. */
  const mirar = useCallback(() => {
    if (!passo) return
    const seletor = seletorDe(passo)
    const alvo = seletor ? document.querySelector<HTMLElement>(seletor) : null
    if (!alvo) {
      // Não declara desaparecido na primeira tentativa: a tela pode estar
      // carregando, ou o formulário abrindo. Quem decide é `esperar`, abaixo.
      setCaixa(null)
      return false
    }
    setSumiu(false)
    const r = alvo.getBoundingClientRect()
    setCaixa({
      topo: r.top - FOLGA,
      esquerda: r.left - FOLGA,
      largura: r.width + FOLGA * 2,
      altura: r.height + FOLGA * 2,
    })
    return true
  }, [passo])

  /**
   * Espera o alvo aparecer antes de dizer que ele não existe.
   *
   * A tela pode estar buscando dados, e o formulário genérico só desenha os
   * campos depois que alguém abre um registro. Declarar "sumiu" no primeiro
   * quadro acusaria defeito onde há só latência — e um aviso falso gasta a
   * credibilidade de que o aviso verdadeiro precisa.
   */
  useEffect(() => {
    if (!passo) return
    let tentativas = 0
    const t = setInterval(() => {
      if (mirar() || ++tentativas > 20) {
        clearInterval(t)
        if (tentativas > 20) setSumiu(true)
      }
    }, 100)
    return () => clearInterval(t)
  }, [passo, mirar])

  // Rolar até o alvo quando ele aparece. `caixa` muda quando `mirar` acerta,
  // e é aí que vale rolar — antes disso não há para onde.
  useEffect(() => {
    if (!passo || !caixa) return
    const seletor = seletorDe(passo)
    document.querySelector<HTMLElement>(seletor ?? '')?.scrollIntoView({
      block: 'center',
      behavior: 'smooth',
    })
    // Só na entrada do passo: rolar a cada medição brigaria com a pessoa
    // tentando rolar a página.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passo])

  useLayoutEffect(() => {
    mirar()
  }, [mirar])

  useEffect(() => {
    const remedir = () => {
      mirar()
    }
    window.addEventListener('resize', remedir)
    window.addEventListener('scroll', remedir, true)
    return () => {
      window.removeEventListener('resize', remedir)
      window.removeEventListener('scroll', remedir, true)
    }
  }, [mirar])

  const avancar = useCallback(() => {
    if (ultimo) aoFechar()
    else setI((n) => n + 1)
  }, [ultimo, aoFechar])

  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') aoFechar()
      if (e.key === 'ArrowRight') avancar()
      if (e.key === 'ArrowLeft') setI((n) => Math.max(0, n - 1))
    }
    window.addEventListener('keydown', tecla)
    return () => window.removeEventListener('keydown', tecla)
  }, [aoFechar, avancar])

  // O foco vai para o balão, senão o leitor de tela continua lendo a página
  // atrás e quem navega por teclado não alcança "Próximo".
  useEffect(() => {
    balaoRef.current?.focus()
  }, [i])

  if (!passo) return null

  /**
   * Onde o balão cabe. Abaixo do alvo quando há espaço, acima quando não há —
   * e no meio da tela quando não há alvo nenhum.
   */
  const estilo: React.CSSProperties = caixa
    ? caixa.topo + caixa.altura + 200 < window.innerHeight
      ? { top: caixa.topo + caixa.altura + 10, left: Math.max(16, caixa.esquerda) }
      : { bottom: window.innerHeight - caixa.topo + 10, left: Math.max(16, caixa.esquerda) }
    : { top: '40%', left: '50%', transform: 'translateX(-50%)' }

  return (
    <div className="tour no-print">
      {/* O véu escurece a página e some sob o alvo, que é o que faz o olho ir
          até ele sem precisar de seta. Cliques nele fecham o tour. */}
      <button
        type="button"
        className="tour__veu"
        aria-label="Fechar o tour"
        onClick={aoFechar}
        style={
          caixa
            ? {
                clipPath: `polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 0,
                  ${caixa.esquerda}px ${caixa.topo}px,
                  ${caixa.esquerda}px ${caixa.topo + caixa.altura}px,
                  ${caixa.esquerda + caixa.largura}px ${caixa.topo + caixa.altura}px,
                  ${caixa.esquerda + caixa.largura}px ${caixa.topo}px,
                  ${caixa.esquerda}px ${caixa.topo}px)`,
              }
            : undefined
        }
      />

      {caixa && (
        <span
          className="tour__alvo"
          aria-hidden
          style={{
            top: caixa.topo,
            left: caixa.esquerda,
            width: caixa.largura,
            height: caixa.altura,
          }}
        />
      )}

      <div
        className="tour__balao"
        style={estilo}
        role="dialog"
        aria-modal="true"
        aria-label={`Passo ${i + 1} de ${passos.length}`}
        tabIndex={-1}
        ref={balaoRef}
      >
        <p className="tour__contagem mono">
          {i + 1} de {passos.length}
        </p>

        {sumiu ? (
          <p className="tour__texto">
            <strong>Esta parte da tela mudou de lugar.</strong> O passo dizia:{' '}
            {passo.texto.charAt(0).toLowerCase() + passo.texto.slice(1)}
          </p>
        ) : (
          <p className="tour__texto">{passo.texto}</p>
        )}

        <div className="tour__acoes">
          <button type="button" className="btn btn--ghost" onClick={aoFechar}>
            Sair
          </button>
          {i > 0 && (
            <button type="button" className="btn btn--ghost" onClick={() => setI((n) => n - 1)}>
              Voltar
            </button>
          )}
          <button type="button" className="btn btn--primary" onClick={avancar}>
            {ultimo ? 'Terminar' : 'Próximo'}
          </button>
        </div>
      </div>
    </div>
  )
}
